import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api, ApiError } from '../api.js';
import { navigate, setFilters } from '../router.js';
import { diffHeadline, isReadable, scenarioState, summarize } from '../lib/scenarios.js';
import { count, mediumDate, since } from '../lib/format.js';

/**
 * The Scenarios page (PRD 7.5, 9; TRD 10.3; Phase 35): every saved what-if, what each one
 * changes, and the one control that matters — showing one.
 *
 * A scenario is not a view. It is a second set of numbers for the views there already are, so
 * this page owns no chart: it owns the list, the assumptions, the recompute, and the act of
 * *selecting* one, after which the ribbon, the heatmap, the timeline, the matrix, the bench and
 * the funnel are all drawn from the scenario's run rather than the base run. That selection
 * lives in the URL like every other filter, so a scenario is a link a planner can send.
 *
 * Three states a reader acts on differently, and they never read the same (`lib/scenarios.js`):
 * a scenario with no run has no numbers at all and cannot be shown; a scenario whose inputs
 * moved after its last run has numbers that answer an older question and says so on the button;
 * a computed scenario carries the one line worth carrying — how its demand and capacity differ
 * from the base run it was asked against.
 */
class ScenarioList extends StoreMixin(LitElement) {
  static properties = {
    _scenarios: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _writeError: { state: true },
    _busy: { state: true },
    _expanded: { state: true },
    _creating: { state: true },
    _name: { state: true },
    _confirming: { state: true },
    _diffs: { state: true },
  };

  constructor() {
    super();
    this._scenarios = null;
    this._loading = false;
    this._error = null;
    this._writeError = null;
    this._busy = new Set();
    this._expanded = new Set();
    this._creating = false;
    this._name = '';
    this._confirming = null;
    this._diffs = new Map();
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 22px;
      }
      .head {
        display: flex;
        align-items: baseline;
        gap: 12px 18px;
        flex-wrap: wrap;
        margin-bottom: 6px;
      }
      h2 {
        margin: 0;
        font-size: 18px;
        color: var(--ink-strong);
      }
      .lede {
        margin: 0 0 16px;
        font-size: 13px;
        color: var(--ink-muted);
        max-width: 78ch;
      }
      .actions {
        margin-left: auto;
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
      button {
        font: inherit;
        font-size: 12.5px;
        padding: 5px 10px;
        border-radius: 6px;
        border: 1px solid var(--border);
        background: var(--surface);
        color: var(--ink);
        cursor: pointer;
      }
      button.primary {
        background: var(--link);
        border-color: var(--link);
        color: var(--surface);
      }
      button.danger {
        color: var(--status-overloaded);
      }
      button.link {
        border: 0;
        background: none;
        color: var(--link);
        padding: 0;
      }
      button:disabled {
        opacity: 0.5;
        cursor: default;
      }
      input[type='text'] {
        font: inherit;
        font-size: 13px;
        padding: 5px 8px;
        border: 1px solid var(--border);
        border-radius: 6px;
        background: var(--surface);
        color: var(--ink);
        min-width: 18em;
      }
      ul.list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 12px;
      }
      li.row {
        border: 1px solid var(--border-soft);
        border-radius: 10px;
        padding: 14px 16px;
      }
      li.row.showing {
        border-color: var(--link);
        box-shadow: inset 3px 0 0 var(--link);
      }
      .name {
        font-size: 14.5px;
        font-weight: 600;
        color: var(--ink-strong);
      }
      .desc {
        font-size: 12.5px;
        color: var(--ink-muted);
        margin: 2px 0 0;
      }
      .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin: 10px 0 0;
      }
      .chip {
        font-size: 11.5px;
        padding: 2px 8px;
        border-radius: 999px;
        border: 1px solid var(--border-soft);
        background: var(--surface-2);
        color: var(--ink-muted);
      }
      .state {
        font-size: 11.5px;
        padding: 2px 8px;
        border-radius: 999px;
        border: 1px solid var(--border-soft);
        white-space: nowrap;
      }
      .state.good {
        color: var(--status-healthy);
        border-color: var(--status-healthy);
      }
      .state.warn {
        color: var(--status-caution);
        border-color: var(--status-caution);
      }
      .state.bad {
        color: var(--status-overloaded);
        border-color: var(--status-overloaded);
      }
      .meta {
        font-size: 12.5px;
        color: var(--ink-muted);
        margin-top: 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 4px 14px;
      }
      .delta {
        font-variant-numeric: tabular-nums;
        color: var(--ink);
      }
      .editor {
        margin-top: 14px;
        padding-top: 14px;
        border-top: 1px solid var(--border-soft);
      }
      .error {
        color: var(--status-overloaded);
        font-size: 12.5px;
        margin: 8px 0 0;
      }
      .empty {
        font-size: 13px;
        color: var(--ink-muted);
      }
      .confirm {
        margin-top: 10px;
        font-size: 12.5px;
        display: flex;
        gap: 8px;
        align-items: center;
        flex-wrap: wrap;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    this._loading = true;
    try {
      const body = await api.get('/scenarios');
      // The list carries no overrides; the open card asks for its own, so the page is one
      // request until somebody opens something.
      this._scenarios = body.scenarios;
      this._error = null;
      store.set({ scenarios: body.scenarios });
    } catch (err) {
      this._error = err instanceof ApiError ? err.message : String(err);
    } finally {
      this._loading = false;
    }
  }

  async _reload(id) {
    const body = await api.get(`/scenarios/${id}`);
    this._scenarios = (this._scenarios ?? []).map((s) => (s.id === id ? body.scenario : s));
    store.set({ scenarios: this._scenarios });
  }

  _busyWith(id, on) {
    const next = new Set(this._busy);
    if (on) next.add(id);
    else next.delete(id);
    this._busy = next;
  }

  async _create() {
    const name = this._name.trim();
    if (!name) return;
    this._writeError = null;
    try {
      const body = await api.post('/scenarios', { name });
      this._name = '';
      this._creating = false;
      this._scenarios = [body.scenario, ...(this._scenarios ?? [])];
      this._expanded = new Set([...this._expanded, body.scenario.id]);
      await this._reload(body.scenario.id);
    } catch (err) {
      this._writeError = err instanceof ApiError ? err.message : String(err);
    }
  }

  async _recompute(scenario) {
    this._busyWith(scenario.id, true);
    this._writeError = null;
    try {
      await api.post(`/scenarios/${scenario.id}/recompute`, { wait: true });
      await this._reload(scenario.id);
      this._diffs.delete(scenario.id);
      await this._loadDiff(scenario.id);
    } catch (err) {
      this._writeError = err instanceof ApiError ? err.message : String(err);
    } finally {
      this._busyWith(scenario.id, false);
    }
  }

  async _delete(scenario) {
    this._busyWith(scenario.id, true);
    try {
      await api.delete(`/scenarios/${scenario.id}`);
      this._scenarios = (this._scenarios ?? []).filter((s) => s.id !== scenario.id);
      store.set({ scenarios: this._scenarios });
      if (this.appState.filters.scenario === scenario.id) setFilters({ scenario: null });
      this._confirming = null;
    } catch (err) {
      this._writeError = err instanceof ApiError ? err.message : String(err);
    } finally {
      this._busyWith(scenario.id, false);
    }
  }

  /** The one line a computed scenario is worth on this page: how it differs from its base. */
  async _loadDiff(id) {
    if (this._diffs.has(id)) return;
    try {
      const body = await api.get(`/scenarios/${id}/diff`, { dimension: 'company' });
      this._diffs = new Map(this._diffs).set(id, body);
    } catch {
      this._diffs = new Map(this._diffs).set(id, null);
    }
  }

  _toggle(id) {
    const next = new Set(this._expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this._expanded = next;
    if (next.has(id)) {
      this._reload(id);
      this._loadDiff(id);
    }
  }

  /** Show this scenario on every view, or go back to the base forecast. */
  _show(scenario, on) {
    setFilters({ scenario: on ? scenario.id : null });
    if (on) navigate('/');
  }

  _row(scenario) {
    const state = scenarioState(scenario);
    const showing = this.appState.filters.scenario === scenario.id;
    const open = this._expanded.has(scenario.id);
    const busy = this._busy.has(scenario.id);
    const run = scenario.last_run;
    const diff = this._diffs.get(scenario.id);
    const headline = diff ? diffHeadline(diff) : null;
    return html`
      <li class="row ${showing ? 'showing' : ''}">
        <div class="head">
          <div>
            <div class="name">${scenario.name}</div>
            ${scenario.description ? html`<p class="desc">${scenario.description}</p>` : nothing}
          </div>
          <span class="state ${state.tone}">${state.label}</span>
          <span class="actions">
            ${
              isReadable(scenario)
                ? html`
                    <button @click=${() => this._show(scenario, !showing)}>
                      ${showing ? 'Show the base forecast' : 'Show on every view'}
                    </button>
                  `
                : nothing
            }
            <button ?disabled=${busy} @click=${() => this._recompute(scenario)}>
              ${busy ? 'Computing…' : scenario.last_run ? 'Recompute' : 'Compute'}
            </button>
            <button @click=${() => this._toggle(scenario.id)}>
              ${open ? 'Close' : 'Assumptions'}
            </button>
            <button class="danger" @click=${() => (this._confirming = scenario.id)}>Delete</button>
          </span>
        </div>

        <div class="chips">
          ${summarize(scenario).map((c) => html`<span class="chip">${c.label}</span>`)}
        </div>

        <div class="meta">
          ${
            run
              ? html`<span
                  >Computed
                  ${since(run.generated_at)}${
                    run.allocation_count ? ` · ${count(run.allocation_count)} allocation rows` : ''
                  }</span
                >`
              : html`<span>No numbers yet — compute it to see it on a view.</span>`
          }
          ${
            scenario.base_run
              ? html`<span
                  >Against the base run of ${mediumDate(scenario.base_run.planning_date)}</span
                >`
              : nothing
          }
          ${headline ? html`<span class="delta">${headline.text}</span>` : nothing}
          ${
            scenario.stale
              ? html`<span
                  >The assumptions changed after this run: recompute before reading it.</span
                >`
              : nothing
          }
          ${run?.status === 'failed' && run.error ? html`<span>${run.error}</span>` : nothing}
        </div>

        ${
          this._confirming === scenario.id
            ? html`
                <div class="confirm">
                  <span>Delete “${scenario.name}” and the runs that computed it?</span>
                  <button class="danger" ?disabled=${busy} @click=${() => this._delete(scenario)}>
                    Delete it
                  </button>
                  <button class="link" @click=${() => (this._confirming = null)}>Keep it</button>
                </div>
              `
            : nothing
        }
        ${
          open
            ? html`
                <div class="editor">
                  <scenario-editor
                    .scenario=${scenario}
                    @scenario-changed=${() => this._reload(scenario.id)}
                  ></scenario-editor>
                </div>
              `
            : nothing
        }
      </li>
    `;
  }

  render() {
    const scenarios = this._scenarios;
    const canWrite = ['planner', 'admin'].includes(this.appState.user?.effective_role);
    return html`
      <div class="card">
        <div class="head">
          <h2>Scenarios</h2>
          ${
            canWrite
              ? html`
                  <span class="actions">
                    ${
                      this._creating
                        ? html`
                            <input
                              type="text"
                              aria-label="Scenario name"
                              placeholder="What is this scenario asking?"
                              .value=${this._name}
                              @input=${(e) => (this._name = e.target.value)}
                              @keydown=${(e) => e.key === 'Enter' && this._create()}
                            />
                            <button class="primary" @click=${() => this._create()}>Create</button>
                            <button class="link" @click=${() => (this._creating = false)}>
                              Cancel
                            </button>
                          `
                        : html`<button class="primary" @click=${() => (this._creating = true)}>
                            New scenario
                          </button>`
                    }
                  </span>
                `
              : nothing
          }
        </div>
        <p class="lede">
          A scenario is the same forecast over the same records with a few assumptions changed:
          deals left out, a delivery moved, a role mix swapped, a person's hours or a freelancer's
          switched. Computing one writes a run of its own and touches nothing the base forecast
          holds. Show one, and every view is drawn from it until you go back.
        </p>

        ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
        ${this._writeError ? html`<p class="error">${this._writeError}</p>` : nothing}
        ${
          scenarios === null
            ? html`<p class="empty">${this._loading ? 'Loading…' : ''}</p>`
            : scenarios.length
              ? html`<ul class="list">
                  ${repeat(
                    scenarios,
                    (s) => s.id,
                    (s) => this._row(s),
                  )}
                </ul>`
              : html`<p class="empty">
                  No scenarios yet. The first one is usually the question somebody asked in a
                  meeting: what if the two biggest deals land, what if this build slips a month.
                </p>`
        }
      </div>
    `;
  }
}

customElements.define('scenario-list', ScenarioList);
export { ScenarioList };
